import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Present.css';

function CGSucursal({ usuario }) {
    const [sucursales, setSucursales] = useState([]);
    const [formulario, setFormulario] = useState({
        idSucursal: '',
        nombre: '',
        estado: 'A'
    });
    const [modoEdicion, setModoEdicion] = useState(false);
    const [mostrarModal, setMostrarModal] = useState(false);
    const [errores, setErrores] = useState({});
    const [busqueda, setBusqueda] = useState('');

    useEffect(() => {
        cargarSucursales();
    }, []);

    const cargarSucursales = async () => {
        try {
            const response = await axios.get('/api/sucursales');
            setSucursales(response.data);
        } catch (error) {
            console.error('Error al cargar sucursales:', error);
            alert('Error al cargar las sucursales');
        }
    };

    const validarFormulario = () => {
        const nuevosErrores = {};

        // Validar ID Sucursal
        if (!formulario.idSucursal) {
            nuevosErrores.idSucursal = 'El ID de la sucursal es requerido';
        } else if (isNaN(formulario.idSucursal) || formulario.idSucursal <= 0) {
            nuevosErrores.idSucursal = 'El ID debe ser un número positivo';
        }

        // Validar Nombre
        if (!formulario.nombre.trim()) {
            nuevosErrores.nombre = 'El nombre es requerido';
        } else if (formulario.nombre.trim().length > 140) {
            nuevosErrores.nombre = 'El nombre no puede tener más de 140 caracteres';
        }

        // Validar Estado
        if (!formulario.estado) {
            nuevosErrores.estado = 'Debe seleccionar un estado';
        } else if (formulario.estado !== 'A' && formulario.estado !== 'I') {
            nuevosErrores.estado = 'El estado debe ser A (Activo) o I (Inactivo)';
        }

        // Verificar duplicados solo al crear
        if (!modoEdicion) {
            const duplicado = sucursales.find(s => s.idSucursal === parseInt(formulario.idSucursal));
            if (duplicado) {
                nuevosErrores.idSucursal = 'Ya existe una sucursal con ese ID';
            }
        }

        setErrores(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormulario(prev => ({
            ...prev,
            [name]: value
        }));

        // Limpiar error del campo que se está editando
        if (errores[name]) {
            setErrores(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validarFormulario()) {
            return;
        }

        try {
            const datosEnviar = {
                idSucursal: parseInt(formulario.idSucursal),
                nombre: formulario.nombre.trim(),
                estado: formulario.estado
            };

            if (modoEdicion) {
                await axios.put(`/api/sucursales/${formulario.idSucursal}`, datosEnviar);
                alert('Sucursal actualizada exitosamente');
            } else {
                await axios.post('/api/sucursales', datosEnviar);
                alert('Sucursal creada exitosamente');
            }

            cerrarModal();
            cargarSucursales();
        } catch (error) {
            console.error('Error al guardar sucursal:', error);
            alert('Error al guardar la sucursal: ' + (error.response?.data?.mensaje || error.message));
        }
    };

    const handleEditar = (sucursal) => {
        setFormulario({
            idSucursal: sucursal.idSucursal.toString(),
            nombre: sucursal.nombre,
            estado: sucursal.estado
        });
        setModoEdicion(true);
        setMostrarModal(true);
        setErrores({});
    };

    const handleEliminar = async (idSucursal) => {
        if (window.confirm(`¿Está seguro de eliminar la sucursal ${idSucursal}?`)) {
            try {
                await axios.delete(`/api/sucursales/${idSucursal}`);
                alert('Sucursal eliminada exitosamente');
                cargarSucursales();
            } catch (error) {
                console.error('Error al eliminar sucursal:', error);
                alert('Error al eliminar la sucursal');
            }
        }
    };

    const abrirModalNuevo = () => {
        setFormulario({
            idSucursal: '',
            nombre: '',
            estado: 'A'
        });
        setModoEdicion(false);
        setMostrarModal(true);
        setErrores({});
    };

    const cerrarModal = () => {
        setMostrarModal(false);
        setFormulario({
            idSucursal: '',
            nombre: '',
            estado: 'A'
        });
        setModoEdicion(false);
        setErrores({});
    };

    const exportarExcel = () => {
        const datosExportar = sucursalesFiltradas.map(sucursal => ({
            'ID Sucursal': sucursal.idSucursal,
            'Nombre': sucursal.nombre,
            'Estado': sucursal.estado === 'A' ? 'Activo' : 'Inactivo'
        }));

        const ws = XLSX.utils.json_to_sheet(datosExportar);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sucursales');
        XLSX.writeFile(wb, 'sucursales.xlsx');
    };

    const sucursalesFiltradas = sucursales.filter(sucursal =>
        sucursal.idSucursal.toString().includes(busqueda) ||
        sucursal.nombre.toLowerCase().includes(busqueda.toLowerCase())
    );

    const obtenerEstadoTexto = (estado) => {
        return estado === 'A' ? 'Activo' : 'Inactivo';
    };

    const obtenerEstadoColor = (estado) => {
        return estado === 'A' ? '#27ae60' : '#e74c3c';
    };

    return (
        <div className="present-container">
            <div className="header-seccion">
                <h2>Gestión de Sucursales</h2>
                <div className="acciones-header">
                    <input
                        type="text"
                        className="input-busqueda"
                        placeholder="Buscar sucursal..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                    <button className="btn btn-nuevo" onClick={abrirModalNuevo}>
                        ➕ Nueva Sucursal
                    </button>
                    <button className="btn btn-exportar" onClick={exportarExcel}>
                        📊 Exportar a Excel
                    </button>
                </div>
            </div>

            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Nombre</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sucursalesFiltradas.length > 0 ? (
                            sucursalesFiltradas.map(sucursal => (
                                <tr key={sucursal.idSucursal}>
                                    <td>{sucursal.idSucursal}</td>
                                    <td>{sucursal.nombre}</td>
                                    <td>
                                        <span style={{
                                            color: obtenerEstadoColor(sucursal.estado),
                                            fontWeight: 'bold'
                                        }}>
                                            {obtenerEstadoTexto(sucursal.estado)}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className="btn-accion"
                                            onClick={() => handleEditar(sucursal)}
                                            title="Editar"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className="btn-accion"
                                            onClick={() => handleEliminar(sucursal.idSucursal)}
                                            title="Eliminar"
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="sin-datos">
                                    No se encontraron sucursales
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="footer-tabla">
                Total de sucursales: {sucursalesFiltradas.length}
            </div>

            {mostrarModal && (
                <div className="modal-overlay">
                    <div className="modal-contenido">
                        <div className="modal-header">
                            <h3>{modoEdicion ? 'Editar Sucursal' : 'Nueva Sucursal'}</h3>
                            <button className="btn-cerrar" onClick={cerrarModal}>×</button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>ID Sucursal *</label>
                                <input
                                    type="number"
                                    name="idSucursal"
                                    className={`form-input ${errores.idSucursal ? 'form-input-error' : ''}`}
                                    value={formulario.idSucursal}
                                    onChange={handleChange}
                                    disabled={modoEdicion}
                                    placeholder="Ingrese el ID de la sucursal"
                                />
                                {errores.idSucursal && <span className="form-error">{errores.idSucursal}</span>}
                            </div>

                            <div className="form-group">
                                <label>Nombre *</label>
                                <input
                                    type="text"
                                    name="nombre"
                                    className={`form-input ${errores.nombre ? 'form-input-error' : ''}`}
                                    value={formulario.nombre}
                                    onChange={handleChange}
                                    maxLength="140"
                                    placeholder="Ingrese el nombre de la sucursal"
                                />
                                {errores.nombre && <span className="form-error">{errores.nombre}</span>}
                            </div>

                            <div className="form-group">
                                <label>Estado *</label>
                                <select
                                    name="estado"
                                    className={`form-input ${errores.estado ? 'form-input-error' : ''}`}
                                    value={formulario.estado}
                                    onChange={handleChange}
                                >
                                    <option value="A">Activo</option>
                                    <option value="I">Inactivo</option>
                                </select>
                                {errores.estado && <span className="form-error">{errores.estado}</span>}
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-cancelar" onClick={cerrarModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-guardar">
                                    {modoEdicion ? 'Actualizar' : 'Guardar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CGSucursal;
